import { EditOutlined, ExpandMore, SaveOutlined } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { AnyAction } from 'redux';
import { Button } from 'src/components/atoms';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  useChangePasswordMutation,
  useDeleteSessionMutation,
} from 'src/graphql';
import { AppDispatch } from 'src/store';
import { boot, shutdown } from 'src/utils/intercom';
import { validatePassword } from 'src/validators';
import {
  clearSession,
  editPersonalInformation,
  updateAvailability,
} from '../../../creators/User.creators';
import * as Types from '../../../types';
import { HeaderText, SubbodyText } from '../../atomsLegacy';
import { ExclamationIcon } from '../../atomsLegacy/icons/ExclamationIcon';
import { IconWrapper } from '../../atomsLegacy/icons/IconWrapper';
import { InfoIcon } from '../../atomsLegacy/icons/InfoIcon';
import { SubheaderText } from '../../atomsLegacy/typography/SubheaderText';
import { AuthorisedManager, RequiredAuthType } from '../../auth';
import { IconInput } from '../../moleculesLegacy/IconInput';
import { LabelToggle } from '../../moleculesLegacy/LabelToggle';
import { TextBox } from '../../organismsLegacy/TextBox';
import './Settings.css';
import { ReportSettingsContainer } from './Settings.styles';

export const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useSelector(
    ({ globalUsers: { currentUser } }: Types.State) => ({
      currentUser,
    }),
  );
  const [displayName, setDisplayName] = useState(
    currentUser?.displayName || currentUser?.name || '',
  );
  const [disableEditDisplayName, setDisableEditDisplayName] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [errorConfirmPassword, setErrorConfirmPassword] = useState(false);
  const [errorNewPassword, setErrorNewPassword] = useState('');
  const [showRedirect, setShowRedirect] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const logout = useDeleteSessionMutation({
    onSuccess: () => {
      queryClient.removeQueries();
      setShowRedirect(true);
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Could not delete session.' });
    },
  });

  const password = useChangePasswordMutation({
    onSuccess: () => {
      handleSuccessToast({ toastMessage: 'Password changed successfully' });
    },
    onError: (error: unknown) => {
      handleErrorToast({ toastMessage: `${(error as Error).message}` });
    },
  });

  if (password.isError) {
    return null;
  }

  if (currentUser === null) {
    return null;
  }

  const handleOnChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const key = event.currentTarget.id;
    const { value } = event.currentTarget;

    if (key === 'newPassword') {
      setErrorNewPassword(validatePassword(value));
      setErrorConfirmPassword(confirmNewPassword !== value);
      setNewPassword(value);
    }

    if (key === 'confirmNewPassword') {
      setErrorConfirmPassword(value !== newPassword);
      setConfirmNewPassword(value);
    }

    if (key === 'currentPassword') {
      setCurrentPassword(value);
    }
  };

  const changePassword = async () =>
    await password.mutate({
      input: {
        current: currentPassword,
        password: newPassword,
        confirmation: confirmNewPassword,
      },
    });

  const handleToggle = async (newToggleValue: boolean) => {
    dispatch(updateAvailability(newToggleValue) as unknown as AnyAction);
  };

  const handleDisplayNameClick = () => {
    if (disableEditDisplayName) {
      setDisableEditDisplayName(false);
    } else {
      if (displayName !== (currentUser?.displayName || currentUser?.name)) {
        const { eid, email, name, role, site, department, country } =
          currentUser!;

        dispatch(
          editPersonalInformation({
            eid,
            email,
            name,
            role,
            site,
            department,
            country,
            displayName,
          }),
        );
      }
      setDisableEditDisplayName(true);
    }
  };

  const handleDisplayNameChange = async (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setDisplayName(event.currentTarget.value);
  };

  if (showRedirect) return <Redirect to="/login" />;

  return (
    <div className="routing-body-content">
      <h1 className="container">Settings</h1>

      <AuthorisedManager>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            data-testid={'report-settings'}
          >
            <HeaderText label="Report Settings" />
          </AccordionSummary>
          <AccordionDetails>
            <ReportSettingsContainer>
              <LabelToggle
                testId="report-availability-toggle"
                size="sm"
                label="Availability to receive reports"
                description={
                  'When this option is turned on, App reporters will be able to choose you as a Case Manager ' +
                  'when submitting the report. You will also be eligible as a Case Manager for Open Reporting.'
                }
                handleToggle={handleToggle}
                checked={
                  currentUser && currentUser.available
                    ? currentUser.available
                    : false
                }
              />
              <div>
                <SubheaderText label="Your display name" />
                <p>
                  Your display name will be seen by reporters when they submit a
                  report that&apos;s assigned to you.
                </p>
                <IconInput
                  inputProps={{
                    testId: 'edit-display-name-input',
                    placeholder: 'Your display name',
                    onChange: handleDisplayNameChange,
                    value: displayName,
                    disabled: disableEditDisplayName,
                  }}
                  buttonProps={{
                    testId: 'edit-display-name-button',
                    icon: disableEditDisplayName ? (
                      <EditOutlined />
                    ) : (
                      <SaveOutlined />
                    ),
                    onClick: handleDisplayNameClick,
                  }}
                />
              </div>
            </ReportSettingsContainer>
          </AccordionDetails>
        </Accordion>
      </AuthorisedManager>

      <RequiredAuthType
        authTypes={[
          Types.AuthenticationType.VAULT_LOGIN,
          Types.AuthenticationType.VAULT_2FA,
        ]}
      >
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            data-testid={'change-password'}
          >
            <HeaderText label="Change Password" />
          </AccordionSummary>
          <AccordionDetails>
            <div
              className="password-settings-box"
              data-testid="password-settings-box"
            >
              <div className="password-info">
                <IconWrapper>
                  <InfoIcon />
                </IconWrapper>
                <SubbodyText
                  label="A valid password must be at least 9 characters long and
                  include at least one uppercase letter and one number"
                />
              </div>

              <div className="change-password-container">
                <div className="password-inputs">
                  <form>
                    <div style={{ display: 'none' }}>
                      <TextBox
                        id="hidden-username"
                        placeholder="hidden-password"
                        value=""
                        onChange={() => undefined}
                        autoComplete="username"
                        valid={true}
                        password={false}
                        isWhite={true}
                      />
                    </div>
                    <div className="password-input">
                      <TextBox
                        data-testid="current-password"
                        id="currentPassword"
                        autoComplete="current-password"
                        placeholder="Current Password"
                        onChange={handleOnChange}
                        value={currentPassword}
                        valid
                        password={true}
                      />
                    </div>
                    <div className="password-input">
                      <TextBox
                        data-testid="new-password"
                        id="newPassword"
                        autoComplete="new-password"
                        placeholder="New Password"
                        onChange={handleOnChange}
                        value={newPassword}
                        valid={!errorNewPassword}
                        password={true}
                        aria-errormessage="newPasswordErrorMessage"
                      />
                      {errorNewPassword && (
                        <div
                          id="newPasswordErrorMessage"
                          className="password-error"
                          data-testid="new-password-error"
                        >
                          <ExclamationIcon size={24} />
                          <SubbodyText label="Please enter a valid password" />
                        </div>
                      )}
                    </div>
                    <div className="password-input">
                      <TextBox
                        data-testid="confirm-new-password"
                        id="confirmNewPassword"
                        autoComplete="new-password"
                        placeholder="Confirm New Password"
                        onChange={handleOnChange}
                        value={confirmNewPassword}
                        valid={!errorConfirmPassword}
                        password={true}
                        aria-errormessage="confirmPasswordErrorMessage"
                        enterKeyAction={() => changePassword()}
                        disabled={errorConfirmPassword}
                      />
                      {errorConfirmPassword && (
                        <div
                          id="confirmPasswordErrorMessage"
                          className="password-error"
                          data-testid="confirm-password-error"
                        >
                          <ExclamationIcon size={24} />
                          <SubbodyText label="Passwords don't match" />
                        </div>
                      )}
                    </div>

                    <br />

                    <Button
                      variant="primary"
                      size="medium"
                      disabled={!!errorNewPassword || errorConfirmPassword}
                      tabIndex={0}
                      onClick={() => changePassword()}
                      width="222px"
                      data-testid={'change-password-button'}
                    >
                      Change
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </RequiredAuthType>

      <Accordion expanded={true}>
        <AccordionDetails>
          <Button
            variant="secondary"
            size="medium"
            tabIndex={0}
            data-testid={'logout-button'}
            onClick={async () => {
              shutdown();
              await logout.mutate({});
              dispatch(clearSession());
              boot();
            }}
            width="120px"
          >
            Log out
          </Button>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
